/**
 * Every number of the run, in metres and seconds. They are the original runner's own values (read from its Android
 * build's config objects on 2026-10-16, see tools/ssnumbers.py; 1 original unit = 0.2 m) unless marked ⚠, which
 * means a public/fan-wiki value or our own choice. Nothing else of the original is used.
 */
const U = 0.2; // metres per original unit

export const world = {
  laneWidth: 20 * U,                 // 4 m
  cellDepth: 11.25 * U,              // 2.25 m — the authoring grid
  lanes: 3,
  /** the running surface sits 2 u above the chunk origin in the original; our y = 0 is the road */
  groundOffset: 2 * U
} as const;

export const motor = {
  startSpeed: 110 * U,               // 22 m/s
  endSpeed: 220 * U,                 // 44 m/s
  speedRampSeconds: 50,              // linear; the original takes 179 s — Karolis 2026-10-17: "takes way too long… should be much faster"
  laneChangeSeconds: 0.27,           // linear X curve
  laneChangeSecondsFlying: 0.54,
  laneChangeSecondsFast: 0.18,       // Zap Sideways board (ability_FastLaneChanging, the teleport board's lane ability)
  minTravelBetweenLaneChanges: 1.5 * U,   // a reversal mid-change needs 0.3 m driven since the change began
  sideImpactReturnSeconds: 0.5,           // CancelLaneChanging: 0.5 s per lane width back after a side hit
  sideDepthMin: 8 * U,                    // a side contact this near the front/back is a corner: 1.6 m …
  sideDepthMax: 13 * U,                   // … growing to 2.6 m at top speed (CornerImpactRegionDepthMin/Max)
  gravity: 200 * U,                  // 40 m/s²
  jumpHeight: 20 * U,                // 4 m → 0.89 s in the air
  airJumpHeight: 20 * U,             // CharacterMotorConfig.AirJumpHeight: the second hop of a double jump, 4 m from where you are
  forgivingJumpSeconds: 0.15,        // jump allowed this long after driving off an edge
  diveVelocity: -90 * U,             // −18 m/s, swipe down in the air; lands into a roll
  rollSeconds: 0.6,
  colliderHeight: 9 * U,             // 1.8 m
  colliderHalfWidth: 0.6,            // ⚠ ours (the original runner is 0.4 m; the car is forgiving, like him)
  rollHeightFactor: 0.5,
  lowerImpactMax: 0.33,              // of the collider height
  upperImpactMin: 0.66,
  lowerImpactRatio: 0.72,            // feet above 72 % of an obstacle = trip, not crash
  cornerRegionWidth: 5.66 * U,       // 1.13 m
  surfaceMaxUpwardsSpeed: 75 * U,    // 15 m/s climb on ramps
  skipHolesLookahead: 4 * U,         // 0.8 m: don't fall into a gap shorter than this
  inputQueueSeconds: 0.5,            // an input that can't run now runs as soon as it can, for this long
  stepUp: 3 * U,                     // 0.6 m: small ledges are stepped onto
  treadmillSeconds: 0.4,
  highJumpMultiplier: 2.0,           // Super Sneakers ("highjump" ability) ⚠ jump curve approximated
  superJumpAirJump: 9 * U,           // ability_HighJump._airJumpHeight: with the high jump on, a double jump's second hop is 9 u × 2 = 3.6 m
  glideSeconds: 1.5,                 // Smooth Drift board = the original's glider (ability_GliderDefault._glideTime)
  glideGravity: 25 * U,              // −25 u/s² at the end of the glide curve; it stays until you land
  glideMinGravity: 0.01 * U,         // the glide never pulls weaker than this (GliderAbilityInstance.UpdateDynamicGravity)
  /** share of glideGravity over the glide's 1.5 s: [time, value, inSlope, outSlope] keys of ability_GliderDefault._glidingCurve */
  glideCurve: [[0, 0, -0.160991, -0.160991], [0.779325, 0.120411, 1.361656, 1.361656], [1, 1, 6.883312, 6.883312]] as readonly (readonly [number, number, number, number])[],
  speedAdditions: {                  // Speed / Super Speed boards (min at start speed → max at end speed)
    speed: [25 * U, 10 * U],
    superSpeed: [50 * U, 20 * U]
  },
  speedEaseSeconds: 0.5              // SpeedupPowerEffect._speedAdjustmentDuration: a speed board's addition grows over 0.5 s and fades over 0.5 s
} as const;

export const flight = {
  jetpackHeight: 95 * U,             // 19 m
  jetpackSpeedMultiplier: 2,
  jetpackClimbSeconds: 1.5,
  jetpackCoinsStart: 200 * U,        // coins begin 40 m ahead
  jetpackCoinSpacing: 30 * U,        // 6 m
  jetpackCoinYOffset: 6 * U,
  headstartHeight: 95 * U,
  headstartSpeed: [400 * U, 550 * U, 700 * U] as const,   // HeadStartPowerEffectDefault_1/2/3: 80 / 110 / 140 m/s
  headstartLandSpeedOffset: [0.01, 0.5, 1.0] as const,    // on landing the speed ramp jumps ahead by this share of its length
  headstartLandingItems: ["magnet", "x2", "sneakers"] as const,   // dropped ahead of the landing, shuffled over shuffled lanes (z ⚠ ours)
  headstartSeconds: 5,
  pogoHeight: 160 * U,               // 32 m
  pogoDistance: 300 * U,             // 60 m
  pogoApex: 0.5,
  pogoCoinRows: 15,                  // 3 lanes × 15 = 45 SOL on the way down
  pogoCoinStart: 58 * U,
  pogoCoinYOffset: 2 * U,
  /** ⚠ ours: open road after a flight's touchdown, in seconds at the landing speed (with the 50 s ramp top speed is common; the
   *  original's 72 m landing section leaves 0.66 s at top speed after the 19 m fall) — the World adds empty road until it holds */
  landingReactionSeconds: 1.2,
  preserveBeforeLanding: 600 * U     // road coins in the last 120 m before a flight's landing stay (_preserveWhenFlyoverDistance)
} as const;

export const stumble = {
  seconds: 5.0,                      // StumbleConfig_Default.Duration
  deathDelay: 0.2,                   // second stumble inside the window → crash after this
  effectSeconds: 5.0,
  postReviveInvincibleSeconds: 1.0,  // StumbleBehaviour._postReviveInvincibilityDuration (also after a board/shield save)
  slowFactor: 0.75,                  // ⚠ while stumbling the run slows a touch
  slowSeconds: 0.5
} as const;

export const chaser = {
  minDistance: 10 * U,               // 2 m — right behind you after a stumble
  maxDistance: 30 * U,               // 6 m — the normal gap
  catchupSeconds: 0.7,
  resetSeconds: 1.5,
  laneSmoothSeconds: 0.1,
  heightSmoothSeconds: 0.3,
  jumpFactor: 0.7,                   // he hops 70 % of your hop
  tutorialFallback: 65 * U,          // 13 m
  flyFallback: 0.2,
  trainHitCameraDistance: 32 * U,
  introSeconds: 0                    // the run's initial 5 s stumble window keeps him close at the start (SetInitialStumble)
} as const;

export const camera = {
  offset: { x: 0, y: 33 * U, z: -33 * U },      // 6.6 m up, 6.6 m behind the car
  lateralBias: 0.75,                              // follows 75 % of the car's lateral motion
  smooth: { x: 15, y: 7.5, z: 200 },
  fov: 68,                                        // vertical FOV of the portrait original
  focus: { y: -19.7 * U, z: 50 * U },             // look-at point relative to the camera: 10 m ahead, 3.9 m lower
  flyingOffsetY: 32.4 * U,
  flyingBias: 0.9,
  jumpFollow: 0.53,                               // camera follows about half of a hop
  speedFov: { speed: 20, superSpeed: 25, jetpack: 18, headstart: 9, slow: -14 },
  fovSmooth: 2,
  shakePower: 1.5
} as const;

export const powers = {
  /** duration by upgrade level 0–5 ⚠ wiki: six levels, +5 s each, 30 s max, jetpack 25 s */
  seconds: {
    magnet: [5, 10, 15, 20, 25, 30],
    x2: [5, 10, 15, 20, 25, 30],
    sneakers: [5, 10, 15, 20, 25, 30],
    jetpack: [5, 9, 13, 17, 21, 25]
  },
  upgradeCosts: [500, 1000, 3000, 10000, 60000],   // ⚠ wiki
  boardSeconds: 30,                                // DefaultHoverboardPowerConfig.Levels[0].Duration (in the build)
  boardCooldownSeconds: 5,                         // DefaultHoverboardPowerConfig._cooldown: no new board for 5 s after one ends or breaks
  randomBoardSeconds: 15,                          // RandomBoardPowerConfig: the mystery power-up's random board
  randomBoardPreloads: 2,                          // MainHoverboardSystem.NumberOfRandomBoardPreloads: boards drawn per run, used in turn
  mysteryDoubleJumpSeconds: 15,                    // DoubleJumpPowerConfig
  magnetSpeed: 150 * U,                            // 30 m/s pull
  magnetRadius: 80 * U,                            // 16 m
  sneakersMagnetSpeed: 200 * U,
  boosterBonus: [5, 6, 7] as const,                // ⚠ wiki: Score Booster adds 5, 6 or 7 to the multiplier
  mysteryPlusMultiplier: 10,                       // ⚠ wiki: Mystery power-up may give +10 multiplier
  doubleCoinsSeconds: 15,                          // 2xCoinMysteryPowerConfig
  speedPadBoost: [45 * U, 55 * U],
  speedPadSlowFactor: 0.5
} as const;

export const score = {
  pointsPerMetre: 1,                               // ⚠ (server data in the original; consistent with its mission ladder)
  pointsPerCoin: 1,                                // ⚠
  pointsPerPickup: 0,                              // ⚠
  baseMultiplier: 1,
  maxMissionMultiplier: 30,                        // wiki: mission sets raise it to x30
  maxBoosters: 3
} as const;

export const revive = {
  keyCosts: [1, 2, 4, 8, 16, 32],                  // ⚠ wiki: doubles per revive in a run
  promptSeconds: 7,                                // ⚠
  minDismissSeconds: 0.5,                          // ⚠
  countdownSpeed: 1.75,                            // RunStateCountdown: a 3-2-1 that takes 1.7 s
  cleanupDistance: 250 * U,                        // 50 m of road ahead is cleared
  endRunDelaySeconds: 2,                           // before the results slide in (1 s after a revive offer)
  endRunDelayAfterReviveSeconds: 1
} as const;

export const route = {
  spawnAhead: 1800 * U,                            // 360 m of road is placed ahead
  placeDistance: 2000 * U,
  cleanupBehind: 300 * U,                          // 60 m
  minimumRepeatDistance: 1900 * U,                 // 380 m before the same section may repeat
  rarityMaxDistance: 4000 * U,                     // power-ups get common over the first 800 m
  raritySteepness: 2
} as const;

export const spawning = {
  /** coins float this high above the surface they sit on */
  coinHeight: 4 * U,
  pickupHeight: 5 * U,
  coinSpacing: 30 * U,                             // 6 m along a line ("6 over 150")
  curveCoins: 7,
  /** pickup weights ⚠ ours, in the spirit of the original's spawn sets (most spots are coins) */
  weights: { magnet: 20, x2: 20, sneakers: 16, jetpack: 10, pogo: 8, mystery: 6, key: 3, mysteryBox: 5, board: 4, letter: 6, token: 6 } as Record<string, number>,
  minDistanceBetweenPickups: 150 * U,              // 30 m
  maxKeysPerRun: 3,
  maxBoxesPerRun: 3
} as const;

export const endRun = {
  initialDelay: 0.1,
  multiplierPopDelay: 0.3,
  scoreCountPerThousand: 0.15,
  minCountSeconds: 1.0,
  maxCountSeconds: 1.0,
  coinCountPerHundred: 0.15,
  coinCountupSpacing: 0.0625,
  bonusCoinDelay: 0.2
} as const;

/** Vertical speed that reaches the given height under our gravity. */
export const jumpVelocity = (height: number, gravity = motor.gravity): number => Math.sqrt(2 * gravity * height);
